import type { Config, PrebidJS } from 'prebid.js'

import type { AdTargeting } from '@/modules/adv/types'

import type { ActiveCmps, HbSiteGameInfo, HeaderBiddingOptions } from './types'

export const BIDDER_TIMEOUT = 1500

const GDPR_TIMEOUT = 8000
const GDPR_ACTION_TIMEOUT = 10000

const ID5_PARTNER_ID = 1920

export const REGS = {
    ext: {
        dsa: {
            dsarequired: 1,
            pubrender: 0,
            datatopub: 2,
        },
    },
}

// const DEFAULT_SCHAIN_NODE = {
//     asi: 'playgama.com',
//     sid: 'p_00000000-0000-0000-0000-000000000001',
// }

export const BIDDER_SETTINGS = {
    ix: { storageAllowed: true },
    onetag: { storageAllowed: true },
    equativ: { storageAllowed: true },
    omnidex: { storageAllowed: true },
} satisfies PrebidJS['bidderSettings']

type Ortb2 = NonNullable<Config['ortb2']>

export type Ortb2Input = {
    activeCmps: ActiveCmps
    schainNode?: HeaderBiddingOptions['schainNode']
    targeting?: AdTargeting
    gameInfo?: HbSiteGameInfo | null
}

const isConsentManagementEnabled = (activeCmps: ActiveCmps): boolean =>
    process.env.NODE_ENV === 'production' && (activeCmps.gpp !== 'none' || activeCmps.tcf)

export const buildOrtb2 = ({ activeCmps, targeting, gameInfo }: Ortb2Input): Ortb2 => {
    const siteCat = gameInfo?.cat?.length ? { cat: gameInfo.cat, sectioncat: gameInfo.cat, pagecat: gameInfo.cat } : {}

    const ortb2: Ortb2 = {
        source: {
            // schain: {
            //     ver: '1.0',
            //     complete: 1,
            //     nodes: [
            //         {
            //             ...(schainNode || DEFAULT_SCHAIN_NODE),
            //             hp: 1,
            //         },
            //     ],
            // },
        },
        site: {
            ext: {
                data: { ...targeting },
            },
            ...siteCat,
            ...(gameInfo ? { content: gameInfo } : {}),
        },
    }

    // DSA is an EU (TCF) obligation, so it rides along with the gdpr consent config below.
    if (isConsentManagementEnabled(activeCmps) && activeCmps.tcf) {
        ortb2.regs = REGS
    }

    return ortb2
}

export const getPrebidConfig = (options: HeaderBiddingOptions, activeCmps: ActiveCmps): Partial<Config> => {
    const id5PDParams: Record<string, string> = {}
    if (options.hashedEmail) {
        // https://wiki.id5.io/docs/passing-partner-data-to-id5#normalizing-emails-prior-to-hashing
        id5PDParams[1] = options.hashedEmail
    }
    const id5PDString = new URLSearchParams(id5PDParams).toString()

    // TODO waiting for creds
    // const connectIdHEString = await sha256hex(user.email).toLowerCase()

    const config: Partial<Config> = {
        priceGranularity: 'custom',
        customPriceBucket: {
            buckets: [
                {
                    max: 2.0,
                    increment: 0.01,
                },
                {
                    max: 10.0,
                    increment: 0.05,
                },
                {
                    max: 30.0,
                    increment: 0.5,
                },
                {
                    max: 250.0,
                    increment: 1.0,
                },
            ],
        },
        floors: {
            additionalSchemaFields: {
                context: (bidRequest) => bidRequest?.ortb2Imp?.ext?.context as string,
            },
            enforcement: { enforceJS: true },
            data: {
                currency: 'USD',
                schema: { fields: ['mediaType', 'context'] },
                values: {
                    'banner|*': 0.02, // fallback - floor 0.02$
                    'video|*': 0.02, // fallback - floor 0.02$
                    'banner|h5-auction': 0.5, //  h5 (instl & rwdd) auction - floor 0.5$
                    'banner|banner-auction': 0.1, // banner auction - floor 0.10$
                    'video|instream-auction': 0.25, // video auction - floor 0.25$
                },
            },
        },
        debug: options.debug,
        bidderTimeout: BIDDER_TIMEOUT,
        // we need cache to get valid url link for imasdk
        cache: {
            url: 'https://ssp.playgama.com/v1/cache',
            // Onetag has own cached vast url, but to use it we need do massive changes on GAM side,
            // so this is a legit workaround for such cases
            ignoreBidderCacheKey: true,
        },
        // TODO add default rates for fallback values
        currency: {
            adServerCurrency: 'USD',
        },
        // show only winner bid to GPT
        enableSendAllBids: false,
        consentManagement: {
            strictStorageEnforcement: true, // todo do we need?
        },
        userSync: {
            filterSettings: {
                iframe: {
                    bidders: '*',
                    filter: 'include',
                },
            },
            userIds: [
                {
                    name: 'id5Id',
                    params: {
                        partner: ID5_PARTNER_ID,
                        externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js',
                        pd: id5PDString,
                        canCookieSync: true, // optional, has effect only when externalModuleUrl is used
                        gamTargetingPrefix: 'id5', // optional, when set the ID5 module will set gam targeting paramaters with this prefix
                        exposeTargeting: true, // optional, when set the ID5 module will execute `window.id5tags.cmd` callbacks for custom targeting tags
                    },
                    storage: {
                        type: 'html5', // "html5" is the recommended storage type
                        name: 'id5id', // "id5id" is the recommended storage name
                        expires: 90, // storage lasts for 90 days
                        refreshInSeconds: 7200, // refresh ID every 2 hours to ensure it's fresh
                    },
                },
                // waiting for pixelId
                // {
                //     name: 'connectId',
                //     params: {
                //         pixelId: '0000',
                //         he: connectIdHEString,
                //         puid: this.config.pgAuid,
                //     },
                // },
                {
                    name: 'sharedId',
                    storage: {
                        type: 'html5',
                        name: '_pubcid',
                        expires: 365,
                    },
                },
            ],
            encryptedSignalSources: {
                sources: [
                    {
                        source: ['id5-sync.com', 'pubcid.org'],
                        encrypt: false,
                        customFunc: () => {
                            throw new Error('Should not be called')
                        },
                    },
                ],
                registerDelay: 0,
            },
        },
        enableTIDs: true,
        consistentTIDs: true,
        ortb2: buildOrtb2({ activeCmps, schainNode: options.schainNode }),
    }

    if (!isConsentManagementEnabled(activeCmps)) {
        delete config.consentManagement
    }

    if (config.consentManagement && activeCmps.tcf) {
        config.consentManagement.gdpr = {
            cmpApi: 'iab',
            timeout: GDPR_TIMEOUT,
            actionTimeout: GDPR_ACTION_TIMEOUT,
            defaultGdprScope: true,
            rules: [
                {
                    purpose: 'storage',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
                {
                    purpose: 'basicAds',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
                {
                    purpose: 'measurement',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
                {
                    purpose: 'personalizedAds',
                    enforcePurpose: true,
                    enforceVendor: true,
                    vendorExceptions: ['playgama'],
                },
            ],
        }
    }

    // consentManagementGpp copies the CMP's answer verbatim into ortb2.regs, so an inapplicable
    // section would go out as regs.gpp: "" + regs.gpp_sid: [-1]. Leaving the config out keeps the
    // module inert and the keys absent, which is what "no GPP" is supposed to look like.
    if (config.consentManagement && activeCmps.gpp === 'applicable') {
        config.consentManagement.gpp = {
            cmpApi: 'iab',
            timeout: GDPR_TIMEOUT,
            actionTimeout: GDPR_ACTION_TIMEOUT,
        }
    }

    return config
}
